import SceneList from "../../../../components/Selector/SceneSelector";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { imagesSelector, sceneSelector } from "../../../../stores/studioSelectors";
import ImageList from "../../../../components/List/ImageList";
import { PhotoIcon, ArrowPathIcon } from "@heroicons/react/20/solid";
import { imagesState, refreshTriggerState } from "../../../../stores/studioAtoms";
import Button from "../../../../components/Button/Button";
import { useState, useEffect } from "react";
import { useSnackbar } from "../../../../components/Layout/SnackbarProvider";
import { useParams } from "react-router-dom";
import { wsHost } from "../../../../services/apis/common";
import { uploadFilesToS3 } from "../../../../utils/s3Upload";


const Uploader = () => {
    const images = useRecoilValue(imagesSelector)
    const scene = useRecoilValue(sceneSelector)
    const setImagesState = useSetRecoilState(imagesState);
    const setRefreshTrigger = useSetRecoilState(refreshTriggerState);
    const [imageFiles, setImageFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState({})
    const [uploadCountWord, setUploadCountWord] = useState("")
    const [uploadError, setUploadError] = useState("")
    const { showSnackbar } = useSnackbar();
    const [socket, setSocket] = useState(null)
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("")
    const { schoolId } = useParams();

    useEffect(() => {
        const SOCKET_SERVER_URL = `${wsHost}ws/upload/${schoolId}/`
        const newSocket = new WebSocket(SOCKET_SERVER_URL);

        newSocket.onopen = () => {
            console.log("WebSocket Connected");
        };

        newSocket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            console.log(message);
            setMessage(message.message)
            setMessages((prevMessages) => [...prevMessages, message]);
            // setImagesState([]) // 매번 GET 호출을 하지 않도록
        };

        setSocket(newSocket);

        return () => {
            if (newSocket.readyState === WebSocket.OPEN) {
                newSocket.close();
            }
        }
    }, [setSocket])

    const setImages = (e) => {
        console.log(e.target.files)
        setImageFiles(e.target.files);
        const uploadedFiles = e.target.files;
        const filesInfo = Array.from(uploadedFiles).map(file => ({
            name: file.name,
            size: file.size,
            type: file.type
        }));
        setImagesState([...images, ...filesInfo]);
    }

    const uploadPhotos = async () => {
        if (!scene) {
            showSnackbar("촬영을 선택해주세요")
            return
        }
        if (imageFiles.length === 0) {
            showSnackbar("업로드할 사진을 선택해주세요")
            return
        }
        setMessage("")
        setUploadProgress({})
        setIsUploading(true)
        setUploadError("")

        try {
            const filesArray = Array.from(imageFiles);
            let completedCount = 0;
            const studio_id = JSON.parse(localStorage.getItem("studioId"));

            // Upload files directly to S3 using STS credentials
            const uploadedFiles = await uploadFilesToS3(filesArray, {
                school_id: parseInt(schoolId),
                studio_id: studio_id,
                scene_id: scene.id,
                concurrency: 16, // 16 concurrent uploads
                onProgress: (fileIndex, percentage) => {
                    setUploadProgress(prev => ({
                        ...prev,
                        [fileIndex]: percentage
                    }));
                },
                onFileComplete: (fileIndex, key) => {
                    completedCount++;
                    setUploadCountWord(`업로드 완료: ${completedCount} / ${filesArray.length}`);
                    console.log(`File ${fileIndex} uploaded: ${key}`);
                }
            });

            // Reset state
            setIsUploading(false);
            document.getElementById('file-upload').value = '';
            setImageFiles([]);
            setImagesState([]);
            showSnackbar("모든 사진 업로드 완료. 서버에서 이미지 처리 시작");

        } catch (err) {
            console.error("Upload error:", err);
            showSnackbar("업로드 중 오류 발생");
            setUploadError("업로드 중 오류 발생. 다시 시도해주세요.");
            setIsUploading(false);
        }
    }

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
        showSnackbar("사진 목록을 새로고침합니다");
    }

    return (
        <div className="border p-4 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
                <h1>사진 업로드</h1>
                <button
                    onClick={handleRefresh}
                    className="p-1 rounded hover:bg-gray-100"
                    title="사진 목록 새로고침"
                >
                    <ArrowPathIcon className="h-5 w-5 text-gray-600" />
                </button>
            </div>
            <SceneList isUsedOn={"upload"} fit={true} />
            <div className="col-span-full">
                <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                >
                    <div className={`mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10 ${isUploading ? "" : "hover:bg-gray-100"}`}>
                        <div className="text-center">
                            <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                            <div className="mt-4 flex text-sm leading-6 text-gray-600 justify-center">
                                <span>{isUploading ? "업로드중" : "클릭해서 업로드"}</span>
                                <input
                                    disabled={isUploading}
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    multiple
                                    className="sr-only"
                                    onChange={setImages}
                                />
                            </div>
                            <p className="text-xs leading-5 text-gray-600">PNG, JPG, GIF up to 10MB</p>
                        </div>
                    </div>
                </label>
            </div>
            <br />
            <Button onClick={uploadPhotos} disabled={isUploading}>업로드</Button>
            <div className={`${isUploading ? "" : "hidden"}`}>
                <p className="text-red">업로드 중에는 창을 닫거나 새로고침을 하면 안됩니다.</p><br />
                {uploadCountWord}
                <div className="mt-4">
                    {Object.keys(uploadProgress).length > 0 && (
                        <div className="space-y-2">
                            {Object.entries(uploadProgress).map(([fileIndex, percentage]) => (
                                <div key={fileIndex} className="space-y-1">
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span className="truncate max-w-[200px]">{imageFiles[fileIndex]?.name}</span>
                                        <span>{Math.round(percentage)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="text-red">{uploadError}</div>
            <div className="text-gray-500 mt-1">
                {message}
            </div>
            <ImageList />
        </div>
    )
}

export default Uploader;